import MessageFormat from '@messageformat/core'

import {
  KnockautAction,
  KnockautObject,
  SymconObjectType,
  SymconVariableType,
} from './interfaces'
import { KnockautHandler } from './knockautHandler'

/**
 * ID's from snapshot are given as ID123. We want 123.
 * @param id
 */
export function transformIdToNumber(id: string): number {
  if (id.includes('ID')) {
    id = id.substr(2, id.length)
  }
  return parseInt(id)
}

/**
 * Parses strings and numbers as boolean
 * @param val
 * @returns
 */
export function parseBool(val: string | number): boolean | null {
  if (
    (typeof val === 'string' &&
      (val.toLowerCase() === 'true' || val.toLowerCase() === 'yes')) ||
    val === 1
  )
    return true
  else if (
    (typeof val === 'string' &&
      (val.toLowerCase() === 'false' || val.toLowerCase() === 'no')) ||
    val === 0
  )
    return false
  return null
}

function getSymconVariableTypeName(instance: KnockautObject): string {
  if (instance.data) {
    switch (instance.data.type) {
      case SymconVariableType.BOOLEAN:
        return 'Boolean'
      case SymconVariableType.FLOAT:
        return 'Float'
      case SymconVariableType.INTEGER:
        return 'Integer'
      case SymconVariableType.STRING:
        return 'String'
      default:
        break
    }
  }
  return 'Variable'
}

export function getSymconObjectTypeName(instance: KnockautObject): string {
  switch (instance.type) {
    case SymconObjectType.CATEGORY:
      return 'Ordner'
    case SymconObjectType.EVENT:
      return 'Ereignis'
    case SymconObjectType.INSTANCE:
      if (instance.data && instance.data.moduleName) {
        return instance.data.moduleName
      } else {
        console.log('todo: find instance type', instance)
        return 'Gerät'
      }
    case SymconObjectType.LINK:
      console.log('todo: find instance type for link', instance)
      return 'Link'
    case SymconObjectType.MEDIA:
      return 'Media'
    case SymconObjectType.SCRIPT:
      return 'Skript'
    case SymconObjectType.VARIABLE:
      return getSymconVariableTypeName(instance)
    default:
      break
  }
}

/**
 * The following functions help to resolve FormatStrings mainly used by SymconActions.
 * FormatStrings are descriptions of how an Action should be displayed.
 * The FormatStrings and the following Functions follow the ICU standards (https://formatjs.io/docs/core-concepts/icu-syntax/)
 */
// TODO: Make all this functions work!!
export function resolveFormatString(action: KnockautAction, parameters: any) {
  const caption = action.caption
  const format = action.format
  if (typeof parameters.VISIBLE === 'boolean') {
    parameters.VISIBLE = parameters.VISIBLE.toString()
  }
  const mf = new MessageFormat('de', {
    customFormatters: {
      profile: (v) => _formattedValue(v, parameters.TARGET),
      object: (v, _, args) => _resolveObjectText(v, parameters, args),
      condition: () => _resolveConditionText(parameters),
      action: (v: string) => _resolveActionText(v),
      // add additional custom formatters here (f.ex. 'action' or ''scheduleAction) to display it correctly
      // undefined formats return the actions caption
    },
  })
  let text = null
  try {
    const msg = mf.compile(format)
    text = msg(parameters)
  } catch (ex) {
    // Pass
  }
  if (!text) {
    if (caption.length > 0) {
      const languageCode = 'de'
      if (action?.locale[languageCode][caption]) {
        text = action.locale[languageCode][caption]
      } else {
        text = caption
      }
    } else {
      text = 'Unbekannt'
    }
  }
  return text
}

function _resolveObjectText(value: any, parameters: any, argString: string) {
  const knockautHandler = KnockautHandler.instance
  const args = _parseArguments(argString, parameters)
  let object = knockautHandler.getObjectById(value)
  if (object === null) {
    return 'undefiniert'
  }
  if (args.ident) {
    const result = knockautHandler.localGetObjectByIdent(args.ident, object.id)
    object = result as KnockautObject
  }
  return object.name
}

function _parseArguments(argString: string, parameters) {
  let args = []
  if (typeof argString !== 'undefined') {
    args = argString.split(' ')
  }
  const result: any = {}
  for (let i = 0; i < args.length; i += 2) {
    const key = args[i]
    let value: any = true
    // check if key has a value. If tere are f.ex. 3 parts, the last key is an option and dont has a value. therefore its value is set to true.
    if (args.length > i + 1) {
      value = args[i + 1]
      if (typeof parameters[value] !== 'undefined') {
        value = parameters[value]
      } else if (value.startsWith("'") && value.endsWith("'")) {
        value = value.slice(1, -1)
      }
    }
    result[key] = value
  }
  return result
}

// TODO: Does not work 100% yet
function _formattedValue(value: any, target: number): string {
  let formattedValue = value
  const variable = KnockautHandler.instance.getObjectById(target)
  if (variable) {
    const variableProfile =
      KnockautHandler.instance.getVariableProfile(variable)
    formattedValue = KnockautHandler.instance.getFormattedValueByProfile(
      value,
      variableProfile
    )
  }
  return formattedValue
}

export function getObjectName(objectID: number): string | null {
  const knockautHandler = KnockautHandler.instance
  const obj = knockautHandler.getObjectById(objectID)
  if (obj === null) {
    return null
  }
  const parent = knockautHandler.getObjectById(obj.parentID)
  return obj.name + ' (' + parent.name + ')'
}

function _resolveConditionText(parameters: any): string {
  const comparisonSymbols = ['=', '!=', '>', '>=', '<', '<=']
  const dayOfTheWeekText = [
    '',
    'Montag',
    'Dienstag',
    'Mittwoch',
    'Donnerstag',
    'Freitag',
    'Samstag',
    'Sonntag',
  ]
  if (parameters.CONDITION) {
    const conditionTextArray = []
    const conditions = JSON.parse(parameters.CONDITION)[0].rules

    // resolve variables
    for (const key in conditions.variable) {
      const value = conditions.variable[key]
      conditionTextArray.push(
        getObjectName(value.variableID) +
          ' ' +
          comparisonSymbols[value.comparison] +
          ' ' +
          _formattedValue(value.value, value.variableID)
      )
    }

    // resolve date conditions
    for (const key in conditions.date) {
      const value = conditions.date[key]
      conditionTextArray.push(
        'Aktuelles Datum ' +
          comparisonSymbols[value.comparison] +
          ' ' +
          value.value.day +
          '.' +
          value.value.month +
          '.' +
          (value.value.year ? value.value.year : '')
      )
    }

    // resolve time conditions
    for (const key in conditions.time) {
      const value = conditions.time[key]
      conditionTextArray.push(
        'Aktuelle Uhrzeit ' +
          comparisonSymbols[value.comparison] +
          ' ' +
          this.pad(value.value.hour) +
          ':' +
          this.pad(value.value.minute) +
          ':' +
          this.pad(value.value.second)
      )
    }

    // resolve dayOfTheWeek conditions
    for (const key in conditions.dayOfTheWeek) {
      const value = conditions.dayOfTheWeek[key]
      conditionTextArray.push(
        'Aktueller Wochentag ' +
          comparisonSymbols[value.comparison] +
          ' ' +
          dayOfTheWeekText[value.value]
      )
    }

    return '( ' + conditionTextArray.join(' und ') + ' )'
  } else {
    return 'Kondition erfüllt wird.'
  }
}

// https://www.symcon.de/de/service/dokumentation/entwicklerbereich/sdk-tools/sdk-php/aktionen/#Formatierung
// TODO: Not all cases handled correctly yet
function _resolveActionText(value: string): string {
  try {
    const action = JSON.parse(value)
    const actionID: string = action.actionID
    const actionParameters = action.parameters
    const allActions = KnockautHandler.instance.actions
    const selectedAction = allActions.find((_action: KnockautAction) => {
      return _action.id === actionID
    })
    if (selectedAction) {
      return resolveFormatString(selectedAction, actionParameters)
    }
  } catch (error) {
    return 'Unbekannt'
  }
}
